import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
// xlsx-js-style is a drop-in fork of xlsx with real cell style support
// (bold, alignment, fill, borders) — needed for the monthly report's
// merged/colored header, which the plain xlsx package can't render.
import XLSXStyle from "xlsx-js-style";
import {
  Box,
  Button,
  Container,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Skeleton,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import bg from "../img/splashSceen.png";
import companylogo from "../img/red with white bck.png";

import {
  fetchOrdersByDate,
  fetchMonthlyReport,
} from "../store/features/client/dailyCateringOrderSlice";
import { fetchClients } from "../store/features/client/clientSlice";
import useDebouncedValue from "../hooks/useDebouncedValue";
import MonthlyReportTable from "../components/DailyCateringOrder/MonthlyReportTable";
import { getTreeDepth, collectHeaderCells } from "../utility/monthlyReportHeaders";

// Same theme as the entry form, so every screen feels like one product.
const localTheme = createTheme({
  palette: {
    primary: { main: "#c60800" },
    secondary: { main: "#c60800" }, 
  },
});

const getTodayString = () => new Date().toISOString().split("T")[0];
const getCurrentMonthString = () => new Date().toISOString().slice(0, 7); // "YYYY-MM"

// Abbreviated headers keep each column narrow — full name shows on tap/hover.
// Used whenever the API doesn't send back serviceHeaders (e.g. older backend).
const DEFAULT_MEAL_COLUMNS = [
  { key: "Breakfast", short: "Breakfast" },
  { key: "Lunch", short: "Lunch" },
  { key: "Dinner", short: "Dinner" },
  { key: "Snacks", short: "Snacks" },
  { key: "Supper", short: "Supper" },
];

const STATUS_COLUMNS = [
  { key: "menu3pm", short: "Menu By 3PM", full: "Menu By 3PM" },
  { key: "orderSent", short: "Confirmed by Vendor", full: "Confirmed by Vendor" },
  { key: "orderReceived", short: "Sent to Vendor", full: "Sent to Vendor" },
];

// Status badges keep the red/blue color language from the entry form's
// Yes/No toggles. Meal counts below are now plain text — no color.
const ACTIVE_BG = "#43a047";
const INACTIVE_BG = "#e53935";
const STICKY_BG = "#fff";

// Plain text meal count — bold black for an actual count, muted grey for a
// meal this company doesn't offer, plain grey "0" if offered but unordered.
// An inactive company always shows a hyphen, regardless of stored counts.
function MealCell({ count, active }) {
  const notOffered = count === null || !active;
  const display = notOffered ? "–" : count;
  const label = (
    <Typography
      noWrap
      sx={{
        fontSize: 12.5,
        fontWeight: notOffered || count === 0 ? 400 : 700,
        color: notOffered
          ? "#b7bac2"
          : count === 0
            ? "text.secondary"
            : "text.primary",
      }}
    >
      {display}
    </Typography>
  );
  return (
    <TableCell align="center" sx={{ py: 0.6, maxWidth: 160 }}>
      {typeof count === "string" && count ? (
        <Tooltip title={count} arrow>
          <span>{label}</span>
        </Tooltip>
      ) : (
        label
      )}
    </TableCell>
  );
}

// Small red/green dot shown next to the company name for its active status.
// Positioned absolutely against the first line of text (rather than flexed
// alongside it) so it stays pinned to the first line's vertical center
// whether the name wraps to one line or three.
function ActiveDot({ active }) {
  return (
    <Box
      component="span"
      sx={{
        position: "absolute",
        left: 0,
        top: "0.72em",
        transform: "translateY(-50%)",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: active ? ACTIVE_BG : INACTIVE_BG,
        flexShrink: 0,
      }}
    />
  );
}

function StatusCell({ active }) {
  return (
    <TableCell align="center" sx={{ p: 0.4 }}>
      <Box
        sx={{
          mx: "auto",
          maxWidth: 80,
          borderRadius: 1,
          py: 0.25,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: active ? ACTIVE_BG : INACTIVE_BG,
          color: active ? "#fff" : "#ffffff",
        }}
      >
        {active ? "Yes" : "No"}
      </Box>
    </TableCell>
  );
}

const SKELETON_ROW_COUNT = 8;

function ReportTableSkeletonBody({ columnCount }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: columnCount }).map((__, colIndex) => (
            <TableCell key={colIndex} sx={{ py: 0.9 }}>
              <Skeleton
                variant="text"
                sx={{ fontSize: 13.5 }}
                width={colIndex === 0 ? "85%" : "60%"}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export default function DailyCateringOrderTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const today = getTodayString();
  const [reportType, setReportType] = React.useState("day");
  const [date, setDate] = React.useState(today);
  const [month, setMonth] = React.useState(getCurrentMonthString());
  const [monthlyClientId, setMonthlyClientId] = React.useState("");
  const [cluster, setCluster] = React.useState("cluster1");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const {
    list = [],
    listServiceHeaders,
    listLoading,
    monthlyReport,
    monthlyReportLoading,
  } = useSelector((state) => state.dailyCateringOrder);
  const { items: allClients = [] } = useSelector((state) => state.clientSlice);
  // Monthly report's company dropdown should only offer companies from the
  // currently selected cluster.
  const clusterClients = useMemo(
    () => allClients.filter((c) => c.cluster === cluster),
    [allClients, cluster],
  );

  // The native date input fires onChange for every intermediate value while
  // scrubbing through the year/month picker (not just the final selection),
  // so debounce before firing the actual fetch.
  const debouncedDate = useDebouncedValue(date, 400);

  useEffect(() => {
    if (reportType === "day")
      dispatch(fetchOrdersByDate({ date: debouncedDate, cluster }));
  }, [reportType, debouncedDate, cluster, dispatch]);

  // Needed for the monthly report's company dropdown — cheap enough to just
  // fetch once up front rather than gating it behind reportType.
  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => {
    if (reportType === "month" && month) {
      dispatch(
        fetchMonthlyReport({
          month,
          clientId: monthlyClientId || undefined,
          cluster,
        }),
      );
    }
  }, [reportType, month, monthlyClientId, cluster, dispatch]);

  // Dynamic meal columns from the API when available, otherwise fall back
  // to the default set so older/unchanged backends keep working as before.
  // "Additional Orders" is a free-text catch-all rather than a real meal, so
  // it's pushed to the end regardless of where the backend places it.
  const mealColumns = useMemo(() => {
    const columns =
      listServiceHeaders && listServiceHeaders.length > 0
        ? listServiceHeaders.map((h) => ({ key: h.key, short: h.label }))
        : DEFAULT_MEAL_COLUMNS;
    const isAdditionalOrders = (col) =>
      col.key?.trim().toLowerCase() === "additional orders";
    return [
      ...columns.filter((col) => !isAdditionalOrders(col)),
      ...columns.filter(isAdditionalOrders),
    ];
  }, [listServiceHeaders]);

  const filteredList = useMemo(() => {
    return list.filter((order) => order.cluster === cluster);
  }, [list, cluster]);

  // Advanced-layout branches report each meal once per floor/shift, e.g.
  // path "TIDEL / 10th Floor / Day / Snacks" and "TIDEL / 10th Floor / NIGHT
  // / Snacks" for the same branch. Split those into one row per floor+shift
  // combo (instead of one row per branch) so counts are shown separately
  // rather than summed together. Orders without a "path" on their services
  // (simple layout) pass through unchanged as a single row.
  const expandedList = useMemo(() => {
    const rows = [];
    filteredList.forEach((order) => {
      const services = order.services || [];
      const hasPath = services.some((s) => s.path);
      if (!hasPath) {
        rows.push({ ...order, subLabel: null });
        return;
      }

      const groups = new Map();
      services.forEach((s) => {
        const segments = s.path.split("/").map((seg) => seg.trim());
        const mealName = segments[segments.length - 1];
        let rest = segments.slice(0, -1);
        if (
          rest[0] &&
          order.branch &&
          rest[0].toLowerCase() === order.branch.toLowerCase()
        ) {
          rest = rest.slice(1);
        }
        const subKey = rest.join(" / ") || null;
        if (!groups.has(subKey)) groups.set(subKey, []);
        groups.get(subKey).push({ name: mealName, count: s.count });
      });

      groups.forEach((groupServices, subKey) => {
        rows.push({ ...order, subLabel: subKey, services: groupServices });
      });
    });
    return rows;
  }, [filteredList]);

  // Reset to the first page whenever the underlying data set changes so the
  // user isn't left on a page that no longer has any rows.
  useEffect(() => {
    setPage(0);
  }, [date, cluster]);

  // The company selected for the monthly report may not belong to the newly
  // selected cluster — clear it rather than silently filtering on a stale id.
  useEffect(() => {
    setMonthlyClientId("");
  }, [cluster]);

  const paginatedList = useMemo(
    () =>
      expandedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [expandedList, page, rowsPerPage],
  );

  const handleChangePage = (e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // "Additional Orders" is a free-text note rather than a headcount — show
  // it as-is instead of coercing it to a number (which would blank out text).
  const countFor = (order, mealName) => {
    const match = order.services?.find(
      (s) => s.name?.toLowerCase() === mealName?.toLowerCase(),
    );
    if (!match) return null;
    if (mealName?.trim().toLowerCase() === "additional orders") {
      return match.count === "" || match.count === null || match.count === undefined
        ? 0
        : match.count;
    }
    return Number(match.count) || 0;
  };

  // Missing key on an order means it was saved before the active flag
  // existed — treat that as active, same rule the entry form uses.
  const isOrderActive = (order) =>
    order.active !== undefined ? !!order.active : true;

  const handleExportExcel = () => {
    // Labels/values here mirror exactly what's on screen: short column
    // headers (not the raw meal key), the Active dot as a Yes/No column,
    // and the same "-" / 0 / count rules MealCell uses.
    const header = [
      "Company",
      "Active",
      "Vendor",
      "Branch",
      "Floor / Shift",
      ...mealColumns.map((col) => col.short),
      ...STATUS_COLUMNS.map((col) => col.short),
    ];

    const rows = expandedList.map((order) => {
      const active = isOrderActive(order);
      return [
        order.clientName,
        active ? "Yes" : "No",
        order.vendorName,
        order.branch || "-",
        order.subLabel || "-",
        ...mealColumns.map((col) => {
          const count = countFor(order, col.key);
          return !active || count === null ? "-" : count;
        }),
        ...STATUS_COLUMNS.map((col) =>
          active && order[col.key] ? "Yes" : "No",
        ),
      ];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `Daily_Catering_Order_Report_${date}.xlsx`);
  };

  const handleExportMonthlyExcel = () => {
    if (!monthlyReport) return;
    const { clients = [], rows = [], totals = {} } = monthlyReport;

    // Mirror MonthlyReportTable's header exactly: one shared header block
    // (company band + as many nested levels as the deepest client needs),
    // built with real merged cells instead of repeating each label per column.
    const globalMaxDepth =
      clients.length === 0 ? 1 : Math.max(...clients.map((c) => getTreeDepth(c.columnTree)));
    const totalHeaderRows = 1 + globalMaxDepth;
    const totalLeafCols = clients.reduce((sum, c) => sum + c.leafColumns.length, 0);
    const totalCols = 2 + totalLeafCols;
    const totalRows = totalHeaderRows + rows.length + 1; // + totals row

    const aoa = Array.from({ length: totalRows }, () => Array(totalCols).fill(""));
    const merges = [];

    aoa[0][0] = "Date";
    aoa[0][1] = "Day";
    if (totalHeaderRows > 1) {
      merges.push({ s: { r: 0, c: 0 }, e: { r: totalHeaderRows - 1, c: 0 } });
      merges.push({ s: { r: 0, c: 1 }, e: { r: totalHeaderRows - 1, c: 1 } });
    }

    let colOffset = 2;
    clients.forEach((client) => {
      const leafCount = client.leafColumns.length;
      aoa[0][colOffset] = client.clientName;
      if (leafCount > 1) {
        merges.push({ s: { r: 0, c: colOffset }, e: { r: 0, c: colOffset + leafCount - 1 } });
      }

      collectHeaderCells(client.columnTree, globalMaxDepth).forEach((cell) => {
        const r = cell.row + 1; // +1 for the company band row
        const c = colOffset + cell.colStart;
        aoa[r][c] = cell.label;
        if (cell.colSpan > 1 || cell.rowSpan > 1) {
          merges.push({ s: { r, c }, e: { r: r + cell.rowSpan - 1, c: c + cell.colSpan - 1 } });
        }
      });

      colOffset += leafCount;
    });

    rows.forEach((row, rowIndex) => {
      const r = totalHeaderRows + rowIndex;
      aoa[r][0] = row.date;
      aoa[r][1] = row.day;
      let c = 2;
      clients.forEach((client) => {
        client.leafColumns.forEach((col) => {
          const value = row.values?.[client.clientId]?.[col.key];
          aoa[r][c] = value === null || value === undefined ? "-" : value;
          c += 1;
        });
      });
    });

    const totalsRowIndex = totalHeaderRows + rows.length;
    aoa[totalsRowIndex][1] = "Total";
    let totalsCol = 2;
    clients.forEach((client) => {
      client.leafColumns.forEach((col) => {
        const value = totals?.[client.clientId]?.[col.key];
        aoa[totalsRowIndex][totalsCol] = value === null || value === undefined ? 0 : value;
        totalsCol += 1;
      });
    });

    const worksheet = XLSXStyle.utils.aoa_to_sheet(aoa);
    worksheet["!merges"] = merges;

    // Date/Day always stay the same blue; each company block alternates
    // tan/blue so adjacent companies are easy to tell apart, same as the
    // reference sheet.
    const STICKY_COLOR = "BDE3EE";
    const BAND_COLORS = ["CBB994", "BDE3EE"];
    const colFill = new Array(totalCols).fill(STICKY_COLOR);
    let fillColOffset = 2;
    clients.forEach((client, clientIndex) => {
      const color = BAND_COLORS[clientIndex % BAND_COLORS.length];
      for (let i = 0; i < client.leafColumns.length; i += 1) {
        colFill[fillColOffset + i] = color;
      }
      fillColOffset += client.leafColumns.length;
    });

    const fillFor = (hex) => ({ patternType: "solid", fgColor: { rgb: hex } });
    // A filled cell hides Excel's default gridline behind it, so every
    // colored cell needs its own explicit border to stay visible.
    const MEDIUM_BORDER = { style: "medium", color: { rgb: "595959" } };
    const borderAll = {
      top: MEDIUM_BORDER,
      bottom: MEDIUM_BORDER,
      left: MEDIUM_BORDER,
      right: MEDIUM_BORDER,
    };

    for (let r = 0; r < totalRows; r += 1) {
      const isHeaderRow = r < totalHeaderRows;
      for (let c = 0; c < totalCols; c += 1) {
        const addr = XLSXStyle.utils.encode_cell({ r, c });
        if (!worksheet[addr]) continue;
        if (isHeaderRow) {
          worksheet[addr].s = {
            font: { bold: true, underline: true },
            alignment: { horizontal: "center", vertical: "center" },
            fill: fillFor(colFill[c]),
            border: borderAll,
          };
        } else {
          worksheet[addr].s = {
            font: { bold: true },
            fill: fillFor(colFill[c]),
            border: borderAll,
          };
        }
      }
    }

    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Monthly Report");
    XLSXStyle.writeFile(workbook, `Monthly_Catering_Order_Report_${month}.xlsx`);
  };

  const columnCount = 2 + mealColumns.length + STATUS_COLUMNS.length;
  const hasMonthlyClients = (monthlyReport?.clients?.length || 0) > 0;

  const reportTableHead = (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            backgroundColor: STICKY_BG,
            fontWeight: 800,
            fontSize: 12.5,
            color: "#111",
            maxWidth: 200,
            minWidth: 100,
            verticalAlign: "bottom",
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            zIndex: 3,
          }}
        >
          Company - Branch
        </TableCell>
        <TableCell
          sx={{
            backgroundColor: STICKY_BG,
            fontWeight: 800,
            fontSize: 12.5,
            color: "#111",
            minWidth: 100,
            verticalAlign: "bottom",
            whiteSpace: "nowrap",
          }}
        >
          Vendor
        </TableCell>
        {
        mealColumns.map((col) => (
          <TableCell
            key={col.key}
            align="center"
            sx={{
              backgroundColor: STICKY_BG,
              fontWeight: 800,
              fontSize: 11.5,
              color: "#111",
              minWidth: 46,
              verticalAlign: "bottom",
              whiteSpace: "nowrap",
            }}
          >
            <Tooltip title={col.key} arrow>
              <span>{col.short}</span>
            </Tooltip>
          </TableCell>
        ))
        }
        {
        STATUS_COLUMNS.map((col) => (
          <TableCell
            key={col.key}
            align="center"
            sx={{
              backgroundColor: STICKY_BG,
              fontWeight: 800,
              fontSize: 11.5,
              color: "#111",
              minWidth: 50,
              verticalAlign: "bottom",
              whiteSpace: "nowrap",
            }}
          >
            <Tooltip title={col.full} arrow>
              <span>{col.short}</span>
            </Tooltip>
          </TableCell>
        ))
        }
      </TableRow>
    </TableHead>
  );

  return (
    <ThemeProvider theme={localTheme}>
      <CssBaseline />
      <Box
        sx={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          py: { xs: 3, sm: 5 },
          overflowX: "hidden",
        }}
      >
        <Container maxWidth={reportType === "month" ? false : "lg"} sx={reportType === "month" ? { maxWidth: "98vw" } : undefined}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2, sm: 4 },
              mt: { xs: 2, sm: 5 },
              borderRadius: 3,
              overflowX: "hidden",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={companylogo}
                alt="logo"
                sx={{ width: { xs: 90, sm: 130 }, mb: 1 }}
              />
            </Box>

            <Typography
              variant="h6"
              align="center"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem" } }}
            >
              {reportType === "day" ? "DAILY CATERING ORDER REPORT" : "MONTHLY CATERING ORDER REPORT"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                mt: 2,
                mb: 2,
              }}
            >
              <TextField
                select
                label="Report Type"
                size="small"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                sx={{ flex: { xs:"initial", sm: "initial" }, width: { xs: "100%", sm: 180 } }}
              >
                <MenuItem value="day">Day wise report</MenuItem>
                <MenuItem value="month">Monthly report</MenuItem>
              </TextField>

              <TextField
                select
                label="Cluster"
                size="small"
                value={cluster}
                onChange={(e) => setCluster(e.target.value)}
                sx={{ flex: { xs:'initial', sm: "initial" }, width: { xs: "100%", sm: 160 } }}
              >
                <MenuItem value="cluster1">Cluster 1</MenuItem>
                <MenuItem value="cluster2">Cluster 2</MenuItem>
              </TextField>

              {reportType === "day" ? (
                <TextField
                  type="date"
                  label="Date"
                  size="small"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    flex: { xs: "initial", sm: "initial" },
                    width: { xs: "auto", sm: 18/0 },
                  }}
                />
              ) : (
                <>
                  <TextField
                    type="month"
                    label="Month"
                    size="small"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: { xs: "initial", sm: "initial" }, width: { xs: "100%", sm: 180 } }}
                  />
                  <TextField
                    select
                    label="Company"
                    size="small"
                    value={monthlyClientId}
                    onChange={(e) => setMonthlyClientId(e.target.value)}
                    sx={{ flex: { xs: "initial", sm: "initial" }, width: { xs: "100%",sm:200 } }}
                  >
                    <MenuItem value="">All companies</MenuItem>
                    {clusterClients.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              {reportType === "day" ? (
                <>
                  {/* Mobile: icon-only export button */}
                  <Tooltip title="Export to Excel">
                    <span style={{ display: "inline-flex" }}>
                      <IconButton
                        onClick={handleExportExcel}
                        disabled={listLoading || expandedList.length === 0}
                        sx={{
                          display: { xs: "inline-flex", sm: "none" },
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          height: 40,
                          width: 40,
                        }}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Desktop: full button with label */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={listLoading || expandedList.length === 0}
                    sx={{
                      display: { xs: "none", sm: "inline-flex" },
                      width: 120,
                      height: 40,
                    }}
                  >
                    Export
                  </Button>

                  {/* Mobile: icon-only new-order button */}
                  <Tooltip title="New Order">
                    <span style={{ display: "inline-flex" }}>
                      <IconButton
                        onClick={() => navigate("/daily-catering-order-form")}
                        sx={{
                          display: { xs: "inline-flex", sm: "none" },
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          height: 40,
                          width: 40,
                        }}
                      >
                        <AddCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Desktop: full button with label */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => navigate("/daily-catering-order-form")}
                    sx={{
                      display: { xs: "none", sm: "inline-flex" },
                      
                      height: 40,
                    }}
                  >
                   Order Form
                  </Button>
                </>
              ) : (                                    
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportMonthlyExcel}
                    disabled={monthlyReportLoading || !hasMonthlyClients}
                    sx={{ width: { xs: "100%", sm: 100 }, height: 40 }}
                  >
                    Export
                  </Button>                                                                                                
                  <Button
                    variant="outlined"                                                     
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => navigate("/daily-catering-order-form")}
                    sx={{ width: { xs: "100%", sm: 150 }, height: 40 }}
                  >
                         Order Form
                  </Button>
                </>
              )}
            </Box>

            {reportType === "month" ? (
              monthlyReportLoading ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
                  ))}
                </Box>
              ) : !hasMonthlyClients ? (
                <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                  No companies match the selected filters for this month.
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Scroll to see all companies. Date/Day stay pinned on the left.
                  </Typography>
                  <MonthlyReportTable report={monthlyReport} />
                </>
              )
            ) : listLoading ? (
              <TableContainer
                sx={{
                  overflow: "auto",
                  border: "1px solid #eee",
                  borderRadius: 2,
                  pb: "18px",
                }}
              >
                <Table size="small">
                  {reportTableHead}
                  <TableBody>
                    <ReportTableSkeletonBody columnCount={columnCount} />
                  </TableBody>
                </Table>
              </TableContainer>
            ) : expandedList.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                {list.length === 0
                  ? "No orders recorded for this date."
                  : "No orders match the selected company filter."}
              </Typography>
            ) : (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: "block", sm: "none" }, mb: 1 }}
                >
                  Scroll to see all companies and meals.
                </Typography>

                <TableContainer
                  sx={{
                    overflow: "auto",
                    border: "1px solid #eee",
                    borderRadius: 2,
                    pb: "18px",
                  }}
                >
                  <Table size="small">
                    {reportTableHead}
                    <TableBody>
                      {paginatedList.map((order) => {
                        const active = isOrderActive(order);
                        const isAdvanced = (order.layoutType || "simple") === "advanced";
                        // Advanced rows are one-per-branch (further split per
                        // floor/shift) and have no _id of their own — only the
                        // parent order's orderId, so key on that plus the
                        // branch and sub-label.
                        const rowKey = isAdvanced
                          ? `${order.orderId || order.client}-${order.branchKey}-${order.subLabel}`
                          : order._id || `${order.client}-${order.vendorId}`;
                        return (
                          <TableRow key={rowKey} hover>
                            <TableCell
                              sx={{
                                maxWidth: 200,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: "#fff",
                              }}
                            >
                              <Box sx={{ position: "relative", pl: 1.75 }}>
                                <ActiveDot active={active} />
                                <Typography
                                  fontWeight={700}
                                  sx={{ fontSize: 11.5, whiteSpace: "normal", wordBreak: "break-word" }}
                                >
                                  {order.branch
                                    ? `${order.clientName} - ${order.branch}`
                                    : order.clientName}
                                  {order.subLabel ? ` (${order.subLabel})` : ""}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography noWrap sx={{ fontSize: 12.5 }}>
                                {order.vendorName || "-"}
                              </Typography>
                            </TableCell>
                            {mealColumns.map((col) => (
                              <MealCell
                                key={col.key}
                                count={countFor(order, col.key)}
                                active={active}
                              />
                            ))}
                            {STATUS_COLUMNS.map((col) => (
                              <StatusCell
                                key={col.key}
                                active={active && !!order[col.key]}
                              />
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={expandedList.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    "& .MuiTablePagination-toolbar": {
                      flexWrap: "wrap",
                      justifyContent: { xs: "center", sm: "flex-end" },
                      alignItems: "center",
                      rowGap: 0.5,
                      pl: 0,
                      pr: 0,
                      minHeight: "auto",
                    },
                    "& .MuiTablePagination-spacer": {
                      display: { xs: "none", sm: "block" },
                    },
                    "& .MuiTablePagination-selectLabel": {
                      display: { xs: "none", sm: "block" },
                      m: 0,
                      lineHeight: "1.5",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      fontSize: { xs: 12, sm: 14 },
                      m: 0,
                      lineHeight: "1.5",
                    },
                    "& .MuiTablePagination-input": {
                      alignItems: "center",
                      my: 0,
                    },
                    "& .MuiTablePagination-select": {
                      fontSize: { xs: 12, sm: 14 },
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-actions": {
                      ml: { xs: 0, sm: 2 },
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}

            <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
              <Typography variant="body2">
                Powered by{" "}
                <Typography component="span" color="primary" fontWeight="bold">
                  HOGIST
                </Typography>{" "}
                © {new Date().getFullYear()} All Rights Reserved
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}


